export type NetworkType = 'mainnet' | 'testnet' | 'custom'

export interface RhoV2ConfigParams {
  network?: NetworkType
  gatewayUrl?: string
  authServiceUrl?: string
}

export interface RhoV2Config {
  network: NetworkType
  gatewayUrl: string
  authServiceUrl: string
}

export interface Currency {
  id: string
  precision: string
}

export interface MarketRiskParams {
  initialMarginThresholdDelta: string
  maintenanceMarginThresholdDelta: string
  marginMarketHedgeFactor: string
  marginRequirementSecondsFloor: string
}

export interface Future {
  id: string
  termStart: string
  termLength: string
  rate: string
  floatIndex: string
  openInterest: string
}

export type RateMathType = 'linear' | 'compounding'

export interface Market {
  id: string
  underlyingCurrency: Currency
  rateMathType: RateMathType
  riskParams: MarketRiskParams
  futures: Future[]
}

export enum OrderSide {
  sell = 'sell',
  buy = 'buy'
}

export enum OrderType {
  limit = 'limit',
  market = 'market'
}

export type TimeInForce = 'GTC' | 'IOC'

export interface CreateOrderRequest {
  userId: string
  portfolioId: string
  symbol: string
  side: OrderSide
  orderType: OrderType
  price?: string // not required for the market order
  qty: string
  timeInForce?: TimeInForce
}

export interface CancelOrderRequest {
  userId: string
  symbol: string
  clientOrderId?: string
  orderId?: string
}

export interface CancelOrderResponse {
  symbol: string
  processingType: string
  seqId: string
  trades: any
  orderExecutionReports: OrderExecutionReport[]
  stateUpdates: any
}

export interface OrderExecutionReport {
  orderId: string
  executionId: string
  userId: string
  orderStatus: any
  side: OrderSide
  price: string
  leavesQty: string
  executedQty: string
  initialQty: string
  lastExecutedPrice: null
  lastExecutedQty: null
  avgExecutedPrice: null
}

export interface OrderStateUpdate {
  type: string | 'order-state-update' | 'user-position-state-update' | 'future-state-update'
  payload: object
}

export interface OrderTrade {
  accumulatedTradesCountAfter: string
  accumulatedVolumeBefore: string
  makerFees: string
  makerPortfolioUserId: string
  makerUserId: string
  openInterestAfter: string
  openInterestBefore: string
  price: string
  priceBefore: string
  seqId: string
  side: OrderSide
  symbol: string
  takerFees: string
  takerPortfolioId: string
  makerPortfolioId: string
  takerUserId: string
  time: string
  volume: string
}

export interface CreateOrderResponse {
  orders: Order[]
  processingType: OrderStatus
  seqId: string
  stateUpdates: OrderStateUpdate
  symbol: string
  time: string
  trades: OrderTrade[]
}

export interface MarginValue {
  total: string
  totalAdjusted: string
  available: string
}

export interface MarginRequirement {
  maintenanceMarginRequirement: string
  adjustedMaintenanceMarginRequirement: string
  initialMarginRequirement: string
  adjustedInitialMarginRequirement: string
}

export enum Direction {
  long = "long",
  short = "short"
}

export interface ConvertedCurrencyValue {
  "initial": string,
  "converted": string
}

export interface Position {
  avgRate: string
  closedFees: ConvertedCurrencyValue
  closedFixedTokens: string,
  closedFloatTradeValue: string
  dv01: ConvertedCurrencyValue
  fees: ConvertedCurrencyValue
  floatTokens: string,
  futureId: string
  isMatured: boolean
  marketId: string
  notional: ConvertedCurrencyValue
  pnl: ConvertedCurrencyValue
  realizedPnL: ConvertedCurrencyValue
  riskDirection: Direction
  totalFixedTokens: string
  totalFloatTradeValue: string
}

export type OrderStatus = 'new' | 'partially-filled' | 'filled' | 'rejected' | 'cancelled'

export interface Order {
  orderId: string
  portfolioId: string
  userId: string
  seqId: string
  marketId: string
  side: OrderSide
  status: OrderStatus
  price: string
  symbol: string
  initialQuantity: string | null
  executedQuantity: string | null
  leavesQuantity: string | null
  lastExecutedQuantity: string | null
  avgExecutedPrice: string | null
  lastExecutedPrice: string | null
  creationTime: string
}

export interface PositionMarginInfo {
  futureId: string
  leverage: string
  liquidationRate: string
  marketId: string
  marginRequirement: ConvertedCurrencyValue
}

export interface UserPortfolio {
  asks: Order[]
  bids: Order[]
  currency: Currency
  margin: MarginValue
  marginRequirements: MarginRequirement
  portfolioId: string
  positionMarginInfos: PositionMarginInfo[]
  positions: Position[]
  profitAndLoss: {
    closedFees: string
    realizedNet: string
    totalNet: string
  }
  seqId: string
  netRiskDirection: 'short' | 'long' | 'none'
  netDv01: string
  leverage: string
}

export type OrderBookSide = 'ask' | 'bid'

export interface OrderItem {
  price: string
  volume: string
}

export interface SymbolStats {
  "symbol": string
  "highPrice": string
  "lowPrice": string
  "lastPrice": string
  "lastOpenInterest": string
  "lastAccumulatedVolume": string
  "lastAccumulatedTradesCount": string
  "lastUpdateTime": string
  "priceChange": string
  "volumeChange": string
  "tradesCountChange": string
  "openInterestChange": string
  "seqId": string
}

export interface OrderBookTrade {
  accumulatedTradesCountAfter: string
  accumulatedVolumeBefore: string
  openInterestAfter: string
  openInterestBefore: string
  price: string
  priceBefore: string
  seqId: string
  side: OrderSide
  symbol: string
  time: string
  volume: string
}

export interface UserTrade {
  fees: string
  portfolioId: string
  price: string
  priceBefore: string
  seqId: string
  side: OrderSide
  symbol: string
  time: string
  userId: string
  volume: string
}

export type OrderBookCandleInterval = '1m' | '5m' | '15m' | '1h' | '1d'

export interface OrderBookCandle {
  accumulatedTradesCountAtOpen: string
  accumulatedVolumeAtClose: string
  accumulatedVolumeAtOpen: string
  closePrice: string
  closeTime: string
  highPrice: string
  interval: string
  lowPrice: string
  openInterestAtClose: string
  openInterestAtOpen: string
  openPrice: string
  openTime: string
  seqId: string
  symbol: string
  tradesCount: string
  volume: string
}

export interface FloatingRateCandle {
  marketId: string
  interval: string
  openTime: string
  closeTime: string
  openRate: string
  highRate: string
  lowRate: string
  closeRate: string
}

export interface JWTTokensPair {
  accessToken: string
  refreshToken: string
  tokenType: string
}

export type Role = 'admin' | 'user'
export interface UserBlockchainAccount {
  id: string
  address: string
}

export interface UserAccount {
  id: string
  roles: Role[]
  blockchainAccounts: UserBlockchainAccount[]
  copperAccounts: any[]
  createdAt: string
  iat: number
  exp: number
  jti: string
}

export interface Candle {
  closeMargin: string
  closeRealizedPnL: string
  closeTime: string
  closeTotalPnL: string
  closeUnrealizedPnL: string
  closedFeesAtClose: string
  closedFeesAtOpen: string
  feesAtClose: string
  feesAtOpen: string
  highMargin: string
  highRealizedPnL: string
  highTotalPnL: string
  highUnrealizedPnL: string
  interval: string
  lowMargin: string
  lowRealizedPnL: string
  lowTotalPnL: string
  lowUnrealizedPnL: string
  openMargin: string
  openRealizedPnL: string
  openTime: string
  openTotalPnL: string
  openUnrealizedPnL: string
  portfolioId: string
  seqId: string
  userId: string
}

export interface FloatingRateCandle {
  closeRate: string
  closeTime: string
  highRate: string
  interval: string
  lowRate: string
  marketId: string
  openRate: string
  openTime: string
}

export interface AllocationUpdate {
  allocationAfter: string
  allocationBefore: string
  currencyId: string
  portfolioId: string
  seqId: string
  time: string
  userId: string
}
